import { useEffect, useState } from "react";
import { AlertTriangle, ExternalLink, Loader2 } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import InvoiceDocument, { FitWidth, InvoicePage2, InvoiceEquipmentPage } from "@/components/invoices/InvoiceDocument";
import { hasPage2, isEquipmentPageJob, prefillFromRecord } from "@/lib/invoice";

const keyFor = (data, re) => Object.keys(data || {}).find((k) => re.test(k));

// Opened when a job is tapped in the Cards view: shows a preview of the invoice.
// If an invoice is already linked to the record it shows that; otherwise it builds a
// live preview straight from the record's fields so there is always something to see.
export default function InvoicePreview({ record, columns, dbId, table, onClose, onOpenCard }) {
  const [state, setState] = useState({ loading: false });
  const data = record?.data || {};
  const specialKey = keyFor(data, /special\s*note/i);
  const special = specialKey ? String(data[specialKey] ?? "").trim() : "";

  useEffect(() => {
    if (!record) return;
    let alive = true;
    setState({ loading: true });
    (async () => {
      try {
        const settings = await api.get("/invoice-settings").then((x) => x.data);
        const r = await api.post("/invoices/for-records", { db_id: dbId, table, ids: [record.id] });
        const invId = r.data?.ids?.[0];
        if (invId) {
          const inv = await api.get(`/invoices/${invId}`).then((x) => x.data);
          if (alive) setState({ loading: false, invId, inv, settings, draft: false });
        } else {
          const inv = prefillFromRecord(record, columns || [], dbId, table, settings);
          if (alive) setState({ loading: false, inv, settings, draft: true });
        }
      } catch (e) {
        if (alive) setState({ loading: false, error: formatError(e) });
      }
    })();
    return () => { alive = false; };
  }, [record, columns, dbId, table]);

  const openFull = () => state.invId && window.open(`/invoices/${state.invId}/print`, "_blank");

  return (
    <Dialog open={!!record} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl bg-card max-h-[92vh] overflow-y-auto" data-testid="invoice-preview-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Invoice preview</DialogTitle>
          {state.draft && <p className="text-sm text-muted-foreground -mt-1" data-testid="preview-draft-note">Preview from record data — this job hasn't been invoiced yet.</p>}
        </DialogHeader>

        {special && (
          <div className="flex gap-2 rounded-lg border border-amber-400 bg-amber-50 dark:bg-amber-950/40 p-3 text-sm" data-testid="preview-special-notes">
            <AlertTriangle size={16} className="text-amber-500 shrink-0 mt-0.5" />
            <div><span className="font-bold text-amber-800 dark:text-amber-300">Special Notes: </span><span className="whitespace-pre-wrap text-black">{special}</span></div>
          </div>
        )}

        <div className="min-h-[280px] min-w-0 overflow-x-hidden">
          {state.loading ? (
            <div className="flex items-center justify-center py-20 text-muted-foreground"><Loader2 className="animate-spin" /></div>
          ) : state.error ? (
            <p className="py-20 text-center text-destructive" data-testid="preview-error">{state.error}</p>
          ) : state.inv ? (
            <div className="space-y-6 bg-slate-200 dark:bg-slate-800 p-4 rounded-lg" data-testid="preview-invoice">
              <FitWidth><InvoiceDocument invoice={state.inv} settings={state.settings} /></FitWidth>
              {isEquipmentPageJob(state.inv.customer?.name) && <FitWidth><InvoiceEquipmentPage invoice={state.inv} settings={state.settings} /></FitWidth>}
              {hasPage2(state.inv) && <FitWidth><InvoicePage2 invoice={state.inv} settings={state.settings} /></FitWidth>}
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => (onOpenCard ? onOpenCard() : onClose())} data-testid="preview-close">Close</Button>
          {state.invId && (
            <Button onClick={openFull} className="hover:bg-blue-600 hover:text-white" data-testid="preview-open-full">
              <ExternalLink size={15} /> Open full invoice
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
